import { client } from "@/api/client";
import type { ApiResponse } from "@/api/error";

export type ProbeStatus = "ok" | "warn" | "fail";

export type ProbeResult = {
  id: string;
  name: string;
  url: string;
  status: ProbeStatus;
  summary: string;
  detail?: string;
  httpStatus?: number;
  elapsedMs: number;
  clockSkewSec?: number;
  error?: string;
};

export type DiagnosticsReport = {
  version: 1;
  ref: string;
  startedAt: string;
  finishedAt: string;
  app: Record<string, string>;
  env: Record<string, string | number | boolean>;
  storage: Record<string, string | number | boolean>;
  features: Record<string, boolean>;
  probes: ProbeResult[];
  errors: CapturedError[];
};

export type CapturedError = { type: string; message: string };

const PROBE_TIMEOUT_MS = 15_000;

const REF_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

export function makeRef(): string {
  const bytes = new Uint8Array(6);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => REF_ALPHABET[b % REF_ALPHABET.length]).join("");
}

const round = (n: number) => Math.round(n * 10) / 10;

const errorMessage = (e: unknown) => (e instanceof Error ? `${e.name}: ${e.message}` : String(e));

export function collectApp(): Record<string, string> {
  return {
    stage: import.meta.env.VITE_STAGE ?? "",
    apiHost: safeHost(import.meta.env.VITE_API_URL),
    pageOrigin: location.origin,
  };
}

function safeHost(url: string | undefined) {
  try {
    return url ? new URL(url).host : "";
  } catch {
    return url ?? "";
  }
}

export function collectEnv(): Record<string, string | number | boolean> {
  const n = navigator;
  const connection = (n as Navigator & { connection?: { effectiveType?: string; rtt?: number; downlink?: number } })
    .connection;
  const env: Record<string, string | number | boolean> = {
    userAgent: n.userAgent,
    platform: n.platform ?? "",
    languages: (n.languages ?? [n.language]).join(", "),
    timezone: safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone, ""),
    utcOffsetMinutes: new Date().getTimezoneOffset(),
    deviceClockUtc: new Date().toISOString(),
    viewport: `${window.innerWidth} x ${window.innerHeight}`,
    screen: `${screen.width} x ${screen.height}`,
    devicePixelRatio: window.devicePixelRatio ?? 1,
    colorScheme: window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light",
    touchPoints: n.maxTouchPoints ?? 0,
    cpuThreads: n.hardwareConcurrency ?? "",
    online: n.onLine,
    connectionHint: connection
      ? `${connection.effectiveType ?? ""} rtt=${connection.rtt}ms down=${connection.downlink}Mbps`
      : "n/a",
    secureContext: window.isSecureContext,
    webdriver: n.webdriver ?? false,
    webgl: webglRenderer(),
  };
  return env;
}

function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

function webglRenderer(): string {
  try {
    const canvas = document.createElement("canvas");
    const gl = (canvas.getContext("webgl") ?? canvas.getContext("experimental-webgl")) as WebGLRenderingContext | null;
    if (!gl) return "unavailable";
    const dbg = gl.getExtension("WEBGL_debug_renderer_info");
    if (!dbg) return "available (renderer masked)";
    return `${gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)} / ${gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL)}`;
  } catch (e) {
    return `error: ${errorMessage(e)}`;
  }
}

export async function collectStorage(): Promise<Record<string, string | number | boolean>> {
  const st: Record<string, string | number | boolean> = {};
  const tryStore = (name: string, fn: () => void) => {
    try {
      fn();
      st[name] = "ok";
    } catch (e) {
      st[name] = `BLOCKED: ${errorMessage(e)}`;
    }
  };
  tryStore("localStorage", () => {
    localStorage.setItem("__diag", "1");
    localStorage.removeItem("__diag");
  });
  tryStore("sessionStorage", () => {
    sessionStorage.setItem("__diag", "1");
    sessionStorage.removeItem("__diag");
  });
  try {
    document.cookie = "__diag=1; path=/; SameSite=Lax";
    st.firstPartyCookies = document.cookie.includes("__diag=1") ? "ok" : "BLOCKED (write silently dropped)";
    document.cookie = "__diag=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  } catch (e) {
    st.firstPartyCookies = `BLOCKED: ${errorMessage(e)}`;
  }
  st.cookieEnabledFlag = navigator.cookieEnabled;
  st.indexedDB = await probeIndexedDb();
  if (navigator.storage?.estimate) {
    try {
      const q = await navigator.storage.estimate();
      st.quotaMB = Math.round((q.quota ?? 0) / 1048576);
      st.usageKB = Math.round((q.usage ?? 0) / 1024);
    } catch {
      st.quotaMB = "unavailable";
    }
  }
  return st;
}

function probeIndexedDb(): Promise<string> {
  if (!window.indexedDB) return Promise.resolve("absent");
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open("__diag", 1);
      req.onsuccess = () => {
        req.result.close();
        indexedDB.deleteDatabase("__diag");
        resolve("ok");
      };
      req.onerror = () => resolve(`BLOCKED: ${req.error?.name ?? "unknown"}`);
      req.onblocked = () => resolve("blocked");
    } catch (e) {
      resolve(`BLOCKED: ${errorMessage(e)}`);
    }
  });
}

export function collectFeatures(): Record<string, boolean> {
  return {
    fetch: typeof fetch === "function",
    webSocket: typeof WebSocket === "function",
    eventSource: typeof EventSource === "function",
    readableStream: typeof ReadableStream === "function",
    abortController: typeof AbortController === "function",
    cryptoSubtle: !!window.crypto?.subtle,
    serviceWorker: "serviceWorker" in navigator,
    clipboardAPI: !!navigator.clipboard?.writeText,
    webAssembly: typeof WebAssembly === "object",
    reducedMotion: !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
  };
}

const withTimeout = <T>(promise: Promise<T>, ms: number, abort: AbortController) =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      abort.abort();
      reject(new Error(`Timed out after ${ms}ms`));
    }, ms);
    promise.then(resolve, reject).finally(() => clearTimeout(timer));
  });

type ProbeSpec = {
  id: string;
  name: string;
  url: string;
  request: (signal: AbortSignal) => Promise<ApiResponse>;
  interpret: (res: ApiResponse, body: string) => { status: ProbeStatus; summary: string; detail?: string };
  onNetworkError?: (message: string) => { status: ProbeStatus; summary: string; detail?: string };
};

async function runProbe(spec: ProbeSpec): Promise<ProbeResult> {
  const abort = new AbortController();
  const t0 = performance.now();
  const base = { id: spec.id, name: spec.name, url: spec.url };
  try {
    const res = await withTimeout(spec.request(abort.signal), PROBE_TIMEOUT_MS, abort);
    const body = await res.text();
    const elapsedMs = round(performance.now() - t0);
    const serverDate = res.headers.get("date");
    const clockSkewSec = serverDate ? Math.round((Date.now() - new Date(serverDate).getTime()) / 1000) : undefined;
    return { ...base, ...spec.interpret(res, body), httpStatus: res.status, elapsedMs, clockSkewSec };
  } catch (e) {
    const message = errorMessage(e);
    const elapsedMs = round(performance.now() - t0);
    const outcome = spec.onNetworkError?.(message) ?? {
      status: "fail" as const,
      summary: "Could not connect",
      detail: message,
    };
    return { ...base, ...outcome, elapsedMs, error: message };
  }
}

const apiUrl = (path: string) => `${import.meta.env.VITE_API_URL}${path}`;

function apiProbe(
  id: string,
  name: string,
  url: string,
  request: (signal: AbortSignal) => Promise<ApiResponse>,
): ProbeSpec {
  return {
    id,
    name,
    url,
    request,
    interpret: (res, body) =>
      res.ok
        ? { status: "ok", summary: "Reachable" }
        : { status: "fail", summary: "Unhealthy", detail: body.slice(0, 300) },
  };
}

function sessionProbe(): ProbeSpec {
  return {
    id: "session",
    name: "Your session",
    url: apiUrl("/user/me"),
    request: (signal) => client.user.me.$get({}, { init: { signal, cache: "no-store" } }),
    interpret: (res, body) => {
      if (res.ok) {
        const email = safe(() => (JSON.parse(body) as { email?: string }).email, undefined);
        return { status: "ok", summary: "Signed in", detail: email };
      }
      if (res.status === 401) return { status: "warn", summary: "Not signed in" };
      return { status: "fail", summary: "Error", detail: body.slice(0, 300) };
    },
  };
}

export function probeSpecs(): ProbeSpec[] {
  return [
    apiProbe("api", "Unbox API", apiUrl("/health"), (signal) =>
      client.health.$get({}, { init: { signal, cache: "no-store" } }),
    ),
    apiProbe("api-db", "Unbox API + database", apiUrl("/health/db"), (signal) =>
      client.health.db.$get({}, { init: { signal, cache: "no-store" } }),
    ),
    sessionProbe(),
  ];
}

export function runProbes(onResult: (result: ProbeResult) => void): Promise<ProbeResult[]> {
  return Promise.all(
    probeSpecs().map(async (spec) => {
      const result = await runProbe(spec);
      onResult(result);
      return result;
    }),
  );
}

export function reportToText(report: DiagnosticsReport): string {
  const lines = [`UNBOX CONNECTION CHECK  ref ${report.ref}  ${report.finishedAt}`, ""];
  const section = (title: string, obj: Record<string, unknown>) => {
    lines.push(`== ${title}`);
    for (const [k, v] of Object.entries(obj)) lines.push(`  ${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`);
    lines.push("");
  };
  lines.push("== Connection checks");
  for (const p of report.probes) {
    const skew = p.clockSkewSec !== undefined ? ` | clock skew ${p.clockSkewSec}s` : "";
    lines.push(`  ${p.name}: ${p.status.toUpperCase()} ${p.summary} | ${p.elapsedMs}ms${skew}`);
    if (p.detail) lines.push(`    ${p.detail}`);
  }
  lines.push("");
  section("App", report.app);
  section("Browser and device", report.env);
  section("Storage", report.storage);
  section("Features", report.features);
  lines.push("== Errors");
  if (!report.errors.length) lines.push("  none");
  for (const e of report.errors) lines.push(`  ${e.type}: ${e.message}`);
  return lines.join("\n");
}
